"use client";

import {
  createContext,
  useContext,
  useState,
  useEffect,
  startTransition,
  type ReactNode,
} from "react";
import type { CityData } from "@/lib/cities-data.types";

const LS_KEY = "ish_city";
const DEFAULT_CITY: CityData = {
  slug: "new-delhi",
  name: "New Delhi",
  state: "Delhi",
  latitude: 28.6139,
  longitude: 77.209,
  timezone: "Asia/Kolkata",
};

interface PanchangCityCtx {
  city: CityData;
  setCity: (city: CityData) => void;
}

const Ctx = createContext<PanchangCityCtx>({
  city: DEFAULT_CITY,
  setCity: () => {},
});

function isStoredCityData(value: Partial<CityData>): value is CityData {
  return (
    typeof value.slug === "string" &&
    typeof value.name === "string" &&
    typeof value.state === "string" &&
    typeof value.latitude === "number" &&
    Number.isFinite(value.latitude) &&
    typeof value.longitude === "number" &&
    Number.isFinite(value.longitude) &&
    typeof value.timezone === "string"
  );
}

export function PanchangCityProvider({ children }: { children: ReactNode }) {
  const [city, setCity] = useState<CityData>(DEFAULT_CITY);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (!raw) return;
      const stored = JSON.parse(raw) as Partial<CityData>;
      if (isStoredCityData(stored)) {
        startTransition(() => setCity(stored));
      }
    } catch {
      // localStorage unavailable or stale
    }
  }, []);

  const handleSetCity = (incoming: CityData) => {
    setCity(incoming);
    try {
      localStorage.setItem(
        LS_KEY,
        JSON.stringify({
          slug: incoming.slug,
          name: incoming.name,
          state: incoming.state,
          latitude: incoming.latitude,
          longitude: incoming.longitude,
          timezone: incoming.timezone,
        })
      );
    } catch {}
  };

  return <Ctx.Provider value={{ city, setCity: handleSetCity }}>{children}</Ctx.Provider>;
}

export function usePanchangCity(): PanchangCityCtx {
  return useContext(Ctx);
}
